import { create } from 'zustand'

export type GlobePhase = 'intro' | 'idle' | 'flying' | 'arrived'

interface FlyToOptions {
  /** Snappier flight for user-initiated taps/search, vs. the slow cinematic intro flight. */
  fast?: boolean
}

/** A unit direction vector (plain numbers, not a THREE.Vector3, to keep store state simple). */
export interface DirectionVector {
  x: number
  y: number
  z: number
}

interface RegionTarget {
  direction: DirectionVector
  distance: number
  /** Revealed only after the camera finishes centering this cluster. */
  focusMemberIds?: string[]
}

interface GlobeState {
  phase: GlobePhase
  targetId: string | null
  /** Bumped on every fly request so the same destination can be re-triggered (e.g. re-searching). */
  flightToken: number
  fast: boolean
  /** True when the pending/current flight is a zoom-out-to-overview rather than a fly-to-place. */
  zoomOut: boolean
  /** Set when the pending/current flight is zooming toward a cluster of places rather than one. */
  regionTarget: RegionTarget | null
  /** Exact members of the numbered bubble currently opened in place. */
  focusedClusterIds: string[]
  /**
   * True while a "Where in the World" round is actively waiting for (or
   * showing the reveal after) a guess — read by Earth.tsx (routes taps to a
   * guess instead of the usual double-tap-to-zoom-out) and Globe.tsx (hides
   * the normal <Markers> layer so every place's dot/label isn't just
   * sitting there to be tapped directly, which would make guessing
   * pointless). GeoGuessOverlay is the only thing that sets this; nothing
   * else in the Canvas cares about it.
   */
  geoGuessActive: boolean
  /** Set by Earth.tsx the instant a guess tap lands; consumed and cleared by GeoGuessOverlay. */
  pendingGuess: { lat: number; lon: number } | null
  /**
   * The correct answer's coordinates + identity, set once a guess is
   * confirmed — paired with `pendingGuess` by GuessLine.tsx to draw the
   * connecting arc, and by AnswerMarker.tsx to show a distinct pin for the
   * real place, during the reveal. Separate from `targetId`/the normal
   * fly-to-a-place flow since the reveal camera move frames *both* points
   * via requestFlyToRegion rather than arriving at one place, and
   * `geoGuessActive` deliberately stays true through the reveal (see
   * GeoGuessOverlay.tsx) so the normal <Markers> layer's clustering bubbles
   * for every other place don't clutter the screen while a kid is looking
   * at just this one result.
   */
  revealTarget: { lat: number; lon: number; emoji: string; name: string } | null
  requestFlyTo: (id: string, options?: FlyToOptions) => void
  requestZoomOut: () => void
  /** Fly toward a direction (e.g. a cluster's centroid) without focusing any single place. */
  requestFlyToRegion: (direction: DirectionVector, distance: number) => void
  requestFlyToCluster: (direction: DirectionVector, distance: number, memberIds: string[]) => void
  focusCluster: (memberIds: string[]) => void
  setPhase: (phase: GlobePhase) => void
  /** Cold-start-only visual preview: selects the first place without starting the WebGL camera. */
  showIntroPreview: (id: string) => void
  /** Plain reset (no animated flight) — for leaving the globe entirely, e.g. back from Explore. */
  clearTarget: () => void
  setGeoGuessActive: (active: boolean) => void
  submitGuess: (lat: number, lon: number) => void
  clearPendingGuess: () => void
  setRevealTarget: (target: { lat: number; lon: number; emoji: string; name: string } | null) => void
}

export const useGlobeStore = create<GlobeState>((set) => ({
  phase: 'intro',
  targetId: null,
  flightToken: 0,
  fast: false,
  zoomOut: false,
  regionTarget: null,
  focusedClusterIds: [],
  geoGuessActive: false,
  pendingGuess: null,
  revealTarget: null,
  requestFlyTo: (id, options) =>
    set((state) => ({
      targetId: id,
      phase: 'flying',
      flightToken: state.flightToken + 1,
      fast: options?.fast ?? false,
      zoomOut: false,
      regionTarget: null,
      focusedClusterIds: [],
    })),
  requestZoomOut: () =>
    set((state) => ({
      targetId: null,
      phase: 'flying',
      flightToken: state.flightToken + 1,
      fast: true,
      zoomOut: true,
      regionTarget: null,
      focusedClusterIds: [],
    })),
  requestFlyToRegion: (direction, distance) =>
    set((state) => ({
      targetId: null,
      phase: 'flying',
      flightToken: state.flightToken + 1,
      fast: true,
      zoomOut: false,
      regionTarget: { direction, distance },
      focusedClusterIds: [],
    })),
  requestFlyToCluster: (direction, distance, memberIds) =>
    set((state) => ({
      targetId: null,
      phase: 'flying',
      flightToken: state.flightToken + 1,
      fast: true,
      zoomOut: false,
      regionTarget: { direction, distance, focusMemberIds: memberIds },
      focusedClusterIds: [],
    })),
  focusCluster: (memberIds) => set({ phase: 'idle', targetId: null, regionTarget: null, focusedClusterIds: memberIds }),
  setPhase: (phase) => set({ phase }),
  showIntroPreview: (id) => set({ targetId: id, phase: 'flying' }),
  clearTarget: () => set({ phase: 'idle', targetId: null, regionTarget: null, focusedClusterIds: [] }),
  // Deliberately does NOT touch pendingGuess — GeoGuessOverlay turns this
  // off (to bring the real <Markers> layer back) at the exact moment it
  // wants to *reveal* the answer next to the guess pin, so the guess must
  // survive that transition. It's cleared explicitly instead, only when a
  // fresh round actually starts.
  setGeoGuessActive: (active) => set({ geoGuessActive: active }),
  submitGuess: (lat, lon) => set({ pendingGuess: { lat, lon } }),
  clearPendingGuess: () => set({ pendingGuess: null }),
  setRevealTarget: (target) => set({ revealTarget: target }),
}))
